<!-- src/views/new-tip/Dashboard.vue -->
<template>
  <v-container fluid>
    <div class="d-flex align-center mb-4">
      <h1 class="text-h5 font-weight-medium">Dashboard New TIP</h1>
      <v-spacer />
      <v-btn outlined :loading="exporting" @click="onExport">
        <v-icon left>mdi-microsoft-excel</v-icon>
        Export Excel
      </v-btn>
    </div>

    <new-tip-filters-bar
      v-model="localFilters"
      :campaigns="campaigns"
      :supervisors="supervisors"
      @apply="onFiltersApply"
    />

    <v-row dense>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Visites soumises" icon="mdi-clipboard-check"
          :value="visitsSoFar" :hint="`sur ${kpis ? kpis.salepoints_total * 2 : 0} possibles`"
          :loading="loading"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Avancement" icon="mdi-progress-check"
          :value="kpis ? kpis.progression_rate : 0" is-percentage
          :hint="`${kpis ? kpis.salepoints_visited : 0} / ${kpis ? kpis.salepoints_total : 0} POS visités`"
          :loading="loading"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Total gains" icon="mdi-cash" icon-color="success"
          :value="kpis ? kpis.total_gain : 0" suffix="pts"
          :loading="loading"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <new-tip-kpi-card
          label="Récompensés Gifty" icon="mdi-gift" icon-color="purple"
          :value="kpis ? kpis.visits_rewarded : 0" :hint="`sur ${kpis ? kpis.visits_validated : 0} validées`"
          :loading="loading"
        />
      </v-col>
    </v-row>

    <v-row dense class="mt-1">
      <v-col cols="12" sm="6" md="3"><new-tip-kpi-card label="Gain GOB"  :value="kpis ? kpis.gain_by_category.gob  : 0" suffix="pts" :loading="loading" /></v-col>
      <v-col cols="12" sm="6" md="3"><new-tip-kpi-card label="Gain West" :value="kpis ? kpis.gain_by_category.west : 0" suffix="pts" :loading="loading" /></v-col>
      <v-col cols="12" sm="6" md="3"><new-tip-kpi-card label="Gain GUB"  :value="kpis ? kpis.gain_by_category.gub  : 0" suffix="pts" :loading="loading" /></v-col>
      <v-col cols="12" sm="6" md="3"><new-tip-kpi-card label="Gain Chap." :value="kpis ? kpis.gain_by_category.hats : 0" suffix="pts" :loading="loading" /></v-col>
    </v-row>

    <v-card outlined class="mt-4" v-if="kpis">
      <v-card-title class="text-subtitle-1">Répartition des gains par catégorie</v-card-title>
      <v-card-text>
        <bar-chart :chart-data="chartData" :chart-options="chartOptions" :height="80" />
      </v-card-text>
    </v-card>

    <v-card outlined class="mt-4">
      <v-card-title class="text-subtitle-1">Détail des visites</v-card-title>
      <v-divider />
      <v-data-table
        :headers="headers"
        :items="visits"
        :loading="loading"
        :server-items-length="meta.total"
        :options.sync="options"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        @update:options="onOptionsChange"
      >
        <template #item.campaign="{ item }">
          <span class="text-caption">{{ item.campaign ? item.campaign.name : '—' }}</span>
        </template>
        <template #item.supervisor="{ item }">
          <div>{{ item.supervisor ? item.supervisor.name : '—' }}</div>
          <div class="text-caption grey--text">{{ item.supervisor ? item.supervisor.phone : '' }}</div>
        </template>
        <template #item.salepoint="{ item }">
          <div>{{ item.salepoint ? item.salepoint.name : '—' }}</div>
          <div class="text-caption grey--text">{{ item.salepoint ? item.salepoint.code : '' }}</div>
        </template>
        <template #item.submitted_at="{ item }">{{ formatDateTime(item.submitted_at) }}</template>
        <template #item.total_gain="{ item }">
          <strong :class="item.total_gain > 0 ? 'success--text' : 'grey--text'">
            {{ formatNumber(item.total_gain) }}
          </strong>
        </template>
        <template #item.status="{ item }">
          <new-tip-status-badge type="visit" :value="item.status" />
        </template>
        <template #item.rewarded="{ item }">
          <v-chip x-small label outlined v-if="item.rewarded" color="purple">
            <v-icon left x-small>mdi-check</v-icon> Payé
          </v-chip>
          <v-chip x-small label outlined v-else-if="item.status === 'validated'" color="orange">
            En attente
          </v-chip>
          <span v-else class="text-caption grey--text">—</span>
        </template>
        <template #item.actions="{ item }">
          <v-btn icon small :to="{ name: 'new-tip.visits.show', params: { id: item.id } }">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { formatDateTime, formatNumber } from '@/helpers/formatters';
import KpiCard from '@/components/new-tip/KpiCard.vue';
import StatusBadge from '@/components/new-tip/StatusBadge.vue';
import FiltersBar from '@/components/new-tip/FiltersBar.vue';
import { Bar as BarChart } from 'vue-chartjs/legacy';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend,
} from 'chart.js';
import api from '@/api/newTipApi';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
  name: 'NewTipDashboard',
  components: {
    'new-tip-kpi-card': KpiCard,
    'new-tip-status-badge': StatusBadge,
    'new-tip-filters-bar': FiltersBar,
    BarChart,
  },
  data() {
    return {
      localFilters: {},
      options: { page: 1, itemsPerPage: 25 },
      exporting: false,
      campaigns: [],
      supervisors: [],
      headers: [
        { text: 'Campagne', value: 'campaign', sortable: false },
        { text: 'Superviseur', value: 'supervisor', sortable: false },
        { text: 'POS', value: 'salepoint', sortable: false },
        { text: 'Date', value: 'submitted_at' },
        { text: 'Total', value: 'total_gain', align: 'right' },
        { text: 'Statut', value: 'status', sortable: false },
        { text: 'Gifty', value: 'rewarded', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'right' },
      ],
      chartOptions: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
      },
    };
  },
  computed: {
    ...mapState('newTip', {
      kpis: s => s.dashboard.kpis,
      visits: s => s.dashboard.visits,
      meta: s => s.dashboard.meta,
      filters: s => s.dashboardFilters,
    }),
    loading() { return this.$store.getters['newTip/isLoading']('dashboard'); },
    visitsSoFar() {
      if (!this.kpis) return 0;
      return this.kpis.visits_validated + this.kpis.visits_submitted + this.kpis.visits_rejected;
    },
    chartData() {
      if (!this.kpis) return { labels: [], datasets: [] };
      return {
        labels: ['GOB', 'West', 'GUB', 'Chap. collector'],
        datasets: [{
          label: 'Gains',
          data: [
            this.kpis.gain_by_category.gob,
            this.kpis.gain_by_category.west,
            this.kpis.gain_by_category.gub,
            this.kpis.gain_by_category.hats,
          ],
          backgroundColor: ['#1976D2', '#388E3C', '#F57C00', '#7B1FA2'],
        }],
      };
    },
  },
  methods: {
    ...mapActions('newTip', ['fetchDashboard', 'exportDashboard']),
    formatDateTime, formatNumber,
    onFiltersApply(filters) {
      this.$store.commit('newTip/SET_DASHBOARD_FILTERS', filters);
      this.options.page = 1;
      this.reload();
    },
    onOptionsChange() { this.reload(); },
    async reload() {
      await this.fetchDashboard({
        ...this.filters,
        page: this.options.page,
        per_page: this.options.itemsPerPage,
      });
    },
    async onExport() {
      this.exporting = true;
      try { await this.exportDashboard(); }
      finally { this.exporting = false; }
    },
    async fetchOptions() {
      try {
        const [campaignsRes, supervisorsRes] = await Promise.all([
          api.listCampaigns({ per_page: 100 }),
          api.listSupervisors(),
        ]);
        this.campaigns = campaignsRes.data.data || [];
        // /api/supervisors retourne { success: true, data: [...] }
        this.supervisors = (supervisorsRes.data && supervisorsRes.data.data) || [];
      } catch (err) { console.error(err); }
    },
  },
  async mounted() {
    this.localFilters = { ...this.filters };
    await this.fetchOptions();
    this.reload();
  },
};
</script>
